import React, { useCallback, useEffect, useMemo, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";

import { Stack } from "@mui/material";
import { RoleComponentGuard } from "components";
import { AccessDenied } from "components/Access-denied/AccessDenied";
import { GlobalModule } from "components/Measure-data";
import { Routes } from "constants/routes";
import { useDataOperationsQueries, useNavigate, useUrlHooks } from "hooks";
import { isEqual } from "lodash";
import { useAccountSelector, useDealActions, useInvestorsSelector, useMonitoringDataFilters, useReportsFiltersSelector } from "store";
import { MeasureDataTableFilters } from "store/types";

import { RoleName } from "../../constants";

export const Monitoring: React.FC = () => {
  const [showTopInvestors, setShowTopInvestors] = useState(false);
  // TODO: cubics in the monitoring page should be saved and fetched from user settings
  const navigate = useNavigate();
  const { queryParams } = useUrlHooks();
  const { pathname } = useLocation();

  const goToData = useCallback(() => {
    navigate({ route: "monitoringData", setQueriesMethod: "replace" });
  }, []);

  const goToDataWide = useCallback(() => {
    navigate({ route: "monitoringDataWide", setQueriesMethod: "replace" });
  }, []);

  const goToFiles = useCallback(() => {
    navigate({ route: "fileRepository", setQueriesMethod: "replace" });
  }, []);

  const goToConsolidation = useCallback(() => {
    navigate({ route: "consolidation", setQueriesMethod: "replace" });
  }, []);

  const goToDebtPayments = useCallback(() => {
    navigate({ route: "debtPayments", setQueriesMethod: "replace" });
  }, []);

  const { setMeasureDataTableFiltersAction: setDataFilters } = useDealActions();
  const { show_global_investor_filter } = useAccountSelector() || {};
  const dataFiltersStore = useMonitoringDataFilters();

  useDataOperationsQueries();

  // Sync URL → store for the data tab. Runs on mount and whenever the URL param changes
  // (e.g. user clicks a different bookmark while already on /monitoringData).
  useEffect(() => {
    if (!matchPath(pathname, Routes.monitoringData)) return;

    const dataOperations = queryParams.dataOperations as string | undefined;
    if (!dataOperations) return;

    try {
      const fromUrl = JSON.parse(dataOperations) as MeasureDataTableFilters;
      if (!isEqual(fromUrl, dataFiltersStore)) {
        setDataFilters(fromUrl);
      }
    } catch {
      // ignore malformed query params
    }
  }, [queryParams.dataOperations, pathname]);

  const filters = useReportsFiltersSelector();
  const investorsFilter = (filters?.Investor as string[]) || [];
  const investors = useInvestorsSelector();
  const pickedInvestors = useMemo(
    () =>
      investors
        .filter((investor) => investorsFilter.includes(investor.name))
        .map((investor) => ({ id: investor.id, name: investor.name })),
    [investors, investorsFilter]
  );

  useEffect(() => {
    if (show_global_investor_filter && investorsFilter.length) {
      setDataFilters({
        investors: pickedInvestors
      });
    }
  }, [investorsFilter, show_global_investor_filter]);

  return (
    <RoleComponentGuard
      availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}
      RenderInstead={<AccessDenied redirectClick={() => navigate({ route: "main" })} title="Back home" />}
    >
      <Stack width="100%" height="100%" alignItems="stretch" gap="18px" overflow="hidden">
        <GlobalModule
          goToData={goToData}
          goToDataWide={goToDataWide}
          goToFiles={goToFiles}
          goToConsolidation={goToConsolidation}
          goToDebtPayments={goToDebtPayments}
          routes={{
            root: {
              from: "monitoring",
              to: "monitoringData"
            },
            data: "monitoringData",
            dataWide: "monitoringDataWide",
            files: "fileRepository",
            aiData: "aiData",
            consolidation: "consolidation",
            debtPayments: "debtPayments"
          }}
          showTopInvestors={showTopInvestors}
          setShowTopInvestors={setShowTopInvestors}
        />
      </Stack>
    </RoleComponentGuard>
  );
};
